import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button, { ButtonVariant } from "../Button";
import { Menu, MenuAnchor, MenuItem, useMenuDismiss } from "./Menu";
import classes from "./record.module.scss";

/** One action in a record header. Every action has a text label and a tooltip. */
export interface RecordAction {
    key: string;
    label: string;
    /** Hover text — what the action does. */
    title: string;
    /** Bootstrap Icons name without the `bi-` prefix. */
    icon?: string;
    /** "primary" for the one main action (Edit on read-first records, the next step on work records). */
    variant?: ButtonVariant;
    onClick?: () => void;
    /** In-app route. */
    to?: string;
    /** Native link — `tel:`, `mailto:` or an external page. */
    href?: string;
    disabled?: boolean;
    /** Why the action is disabled, shown beside it (state gaps stay visible). */
    disabledReason?: string;
    loading?: boolean;
    /** Always list it under "More", even when there is room. */
    inMenu?: boolean;
    /** Stays visible on a narrow (phone) header instead of folding into "More" — e.g. Call. */
    keepOnNarrow?: boolean;
    /**
     * A ready-made control (e.g. CommentsDrawerButton with its unread badge)
     * shown among the visible actions in place of a Button. It never moves
     * into "More".
     */
    element?: React.ReactNode;
}

const DEFAULT_VISIBLE = 4;

/** Opens a native link: tel: / mailto: in place, a web page in a new tab. */
export const openHref = (href: string): void => {
    if (/^https?:/i.test(href)) window.open(href, "_blank", "noopener");
    else window.location.assign(href);
};

/**
 * A visible action — always a Button (the theme styles `button.crx-btn` only),
 * navigating for `to` / `href` actions.
 */
const ActionControl: React.FC<{ action: RecordAction }> = ({ action }) => {
    const navigate = useNavigate();
    if (action.element) return <>{action.element}</>;
    const handleClick = () => {
        if (action.to) navigate(action.to);
        else if (action.href) openHref(action.href);
        else action.onClick?.();
    };
    return (
        <Button
            type="button"
            variant={action.variant}
            icon={action.icon}
            title={action.disabledReason ? `${action.title} — ${action.disabledReason}` : action.title}
            disabled={action.disabled}
            loading={action.loading}
            onClick={handleClick}
        >
            {action.label}
        </Button>
    );
};

/** A "More" menu row for an action. */
const ActionMenuItem: React.FC<{ action: RecordAction; className?: string; onPicked: () => void }> = ({ action, className, onPicked }) => (
    <MenuItem
        title={action.title}
        icon={action.icon}
        danger={action.variant === "danger" || action.variant === "danger-solid"}
        disabled={action.disabled}
        reason={action.disabledReason}
        to={action.to}
        href={action.href}
        className={className}
        onSelect={() => {
            onPicked();
            if (!action.to && !action.href) action.onClick?.();
        }}
    >
        {action.label}
    </MenuItem>
);

interface RecordActionsProps {
    actions: RecordAction[];
    /** How many actions show before the rest move into "More" (default 4). */
    maxVisible?: number;
}

/**
 * A record header's action group: at most `maxVisible` buttons, the rest
 * under "More". On a narrow header (under 576px) only the primary action,
 * `keepOnNarrow` actions and custom elements stay visible; the other visible
 * actions move into "More".
 */
const RecordActions: React.FC<RecordActionsProps> = ({ actions, maxVisible = DEFAULT_VISIBLE }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const close = useCallback(() => setIsOpen(false), []);
    useMenuDismiss(isOpen, wrapperRef, close);

    const candidates = actions.filter((action) => !action.inMenu);
    // The primary action always stays visible; the others fill the remaining slots in order.
    const primaries = candidates.filter((action) => action.variant === "primary");
    const kept = new Set([...primaries, ...candidates.filter((action) => action.variant !== "primary").slice(0, Math.max(0, maxVisible - primaries.length))]);
    const visible = candidates.filter((action) => kept.has(action));
    const overflow = [...candidates.filter((action) => !kept.has(action)), ...actions.filter((action) => action.inMenu)];
    // Buttons that fold into "More" on a narrow header: not the primary, not a custom element.
    const folding = visible.filter((action) => action.variant !== "primary" && !action.element && !action.keepOnNarrow);
    const hasMenu = overflow.length > 0 || folding.length > 0;

    return (
        <div className={classes.actions}>
            {visible.map((action) => {
                const control = <ActionControl action={action} />;
                const isFolding = folding.includes(action);
                return (
                    <React.Fragment key={action.key}>
                        {isFolding ? <span className={classes.secondaryAction}>{control}</span> : control}
                        {action.disabled && action.disabledReason && !isFolding && (
                            <span className={classes.disabledReason}>{action.disabledReason}</span>
                        )}
                    </React.Fragment>
                );
            })}
            {hasMenu && (
                <MenuAnchor ref={wrapperRef} className={overflow.length === 0 ? classes.narrowOnly : ""}>
                    <Button
                        type="button"
                        icon="three-dots"
                        title="More actions"
                        aria-haspopup="menu"
                        aria-expanded={isOpen}
                        onClick={() => setIsOpen((prev) => !prev)}
                    >
                        More
                    </Button>
                    {isOpen && (
                        <Menu label="More actions">
                            {folding.map((action) => (
                                <ActionMenuItem key={`narrow-${action.key}`} action={action} className={classes.narrowOnly} onPicked={close} />
                            ))}
                            {overflow.map((action) => (
                                <ActionMenuItem key={action.key} action={action} onPicked={close} />
                            ))}
                        </Menu>
                    )}
                </MenuAnchor>
            )}
        </div>
    );
};

export default RecordActions;
